import React from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { Icon } from 'src/components/icons'
import { getColor } from 'src/styles'
import { useHandler } from 'src/hooks'
import { Columns, Rows } from '../../layout'
import styles from './Notification.scss'

function Notification({
  title,
  color,
  message,
  icon,
  transitionDuration,
  closable,
  closed,
  duration,
  onClose,
  pausable,
}) {
  // create node ref used to measure its height
  const ref = React.useRef()

  // create timeout ref used to delay the notification closing
  const timeout = React.useRef()

  // create the method responsible of the delayed notification closing
  const closeAfterDelay = useHandler(() => {
    // if duration exists and is not 0, and if notification is not already closed
    if (duration && !closed) {
      // cancel previous timeout
      clearTimeout(timeout.current)
      // and reprogram a new one
      timeout.current = setTimeout(onClose, duration)
    }
  })

  // declare mounted state used to show notification with a transition
  const [mounted, setMounted] = React.useState(false)
  React.useEffect(() => {
    // async is needed in order to display the notification with fade transition
    // (it has to be invisible before being visible to see the transition)
    setTimeout(() => setMounted(true))
  }, [])

  // create mouse handlers
  const onMouseEnter = useHandler(() => pausable && !closed && clearTimeout(timeout.current))
  const onMouseLeave = useHandler(() => pausable && !closed && closeAfterDelay())
  const onClick = useHandler(() => closable && onClose())

  // reprogram closing on each update
  React.useEffect(() => {
    closeAfterDelay()
  })

  return (
    <div
      className={classnames(styles.container, { [styles.hidden]: closed || !mounted })}
      ref={ref}
      style={{
        marginTop: closed && ref.current ? `-${ref.current.scrollHeight}px` : '',
        transform: !mounted && 'translateX(50%)',
        transitionDuration: transitionDuration + 'ms',
      }}
    >
      <Rows
        gap="0"
        style={{ background: getColor(color) }}
        className={classnames([styles.notification, { [styles.closable]: closable }])}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
      >
        {(icon || title) && (
          <Columns layout="auto" align="center" className={styles.header}>
            {icon && <Icon icon={icon} />}
            {title && <div className={styles.title}>{title}</div>}
          </Columns>
        )}
        {message && <div className={styles.message}>{message}</div>}
      </Rows>
    </div>
  )
}

Notification.propTypes = {
  title: PropTypes.node,
  message: PropTypes.node,
  transitionDuration: PropTypes.number,
  icon: PropTypes.elementType,
  color: PropTypes.string,
  closable: PropTypes.bool,
  closed: PropTypes.bool,
  duration: PropTypes.number,
  onClose: PropTypes.func,
  pausable: PropTypes.bool,
}

Notification.defaultProps = {
  title: '',
  message: '',
  transitionDuration: 500,
  icon: '',
  color: '',
  pausable: true,
  closable: true,
  closed: false,
  duration: 5000,
  onClose: () => {},
}

export default React.memo(Notification)
